import { toProductOptions } from "../dropdownOptions";
import type { DropdownOption } from "../dropdownOptions";

/**
 * The page-type step's pick, as PageFormContainer hands it to the template step
 * (a prop since SCRUM-2206; before, a copy in the redux store).
 * `object` is polymorphic per `type` (a path string for the simple types, an
 * entity doc otherwise), so it is `unknown` here and read through
 * PageTypeObject where a branch needs it.
 */
export type ProductStatePageType = { type?: string; object?: unknown };

/** The fields of the category / sub_category selection the narrowing reads. */
type PageTypeObject = {
  /** category: each subcategory's product ids */
  subcategories?: { products?: unknown[] }[];
  /** sub_category: the linked product objects */
  products?: { _id?: unknown }[];
} | null | undefined;

/** A ProductProperties row as the product dropdown lists it. */
type ProductPropertiesRow = {
  _id?: unknown;
  product: { title?: string; linked: unknown[]; sponsored?: boolean };
};

/**
 * Pure helpers for the page-CREATE wizard's coupling to the page-type step
 * (its pick, the `pageTypeSelection` prop), extracted from IndexTemplateSelector so the per-type
 * rendering / building blocks can stay pure (SCRUM-669, Pages step 2b).
 *
 * Two pure concerns live here (pinned by contentVariables.productState.test.tsx):
 *   1. selectProductOptions   — narrow the product dropdown to the active page
 *      type's products (category / sub_category), else the full list.
 *   2. filterTemplatesByPageType — narrow the Design-Template dropdown by page type.
 *
 * The page-type overlay onto a scalar Product/Post content variable (SCRUM-2199,
 * SCRUM-2202) was removed in SCRUM-2205: no live template declares one.
 *
 * The old questionAlert / Alerte SweetAlert was dead (state set, never rendered)
 * and was dropped in the same isolation.
 */

// Which templates the wizard offers per page type (SCRUM-556).
// Primary match is the template's `type` field (set in the template form);
// the name keywords are kept as a fallback for older templates whose type
// does not follow the mapping, so no currently visible template disappears.
const templateFiltersByPageType: Record<string, { types: string[]; keywords: string[] }> = {
  product: { types: ["product_details", "product_simple"], keywords: ["product"] },
  category: { types: ["intro_page"], keywords: ["intro"] },
  sub_category: { types: ["listing_page"], keywords: ["listing"] },
  author: { types: ["author_page"], keywords: ["author"] },
  post: { types: ["blog_page"], keywords: ["article"] },
  simple_page: {
    types: [
      "home_page", "contact_form", "blog_index", "info_article", "redirect_page",
      "error_page", "sitemap_page", "privacy_page", "navigation", "footer",
      "score_method_page",
    ],
    keywords: [
      "contact", "disclaimer", "privacy", "form", "error", "about", "home",
      "redirect", "sitemap", "header", "footer", "intro", "blog",
    ],
  },
};

/**
 * Filter Design-Template options by the active page type. Returns the input
 * unchanged when the page type has no filter (identical to the old inline guard).
 */
export function filterTemplatesByPageType<T extends { label: string; value: { type?: string } }>(
  templateOptions: T[],
  pageTypeType?: string
): T[] {
  const filter = templateFiltersByPageType[pageTypeType as string];
  if (!filter) return templateOptions;
  return templateOptions.filter(
    (t) =>
      filter.types.includes(t.value.type as string) ||
      filter.keywords.some((keyword) => t.label.toLowerCase().includes(keyword))
  );
}

/**
 * Product-dropdown options for the active page type. category / sub_category
 * narrow the list to that page's products; any other page type returns the full
 * list. Same matching/sort semantics as the pre-extraction inline code:
 *   - category: subcategories[].products holds product ids (`id === pp._id`).
 *   - sub_category: object.products holds objects (`obj._id === pp._id`), then
 *     keep only linked products, sorted sponsored-first.
 */
export function selectProductOptions<T extends ProductPropertiesRow>(
  productProperties: T[],
  pageType: ProductStatePageType
): DropdownOption<T>[] {
  // Guard the fields actually read, not just `object`: the create wizard does
  // not reset page_type on mount, so a truthy object without the arrays must
  // degrade to the all-products fallback instead of throwing. (Until
  // SCRUM-1651 the update flow also put a slim `{_id}`-only object here —
  // getPageById selected `_id` only; it now answers the arrays these
  // branches read, and the write boundary refuses a payload without them.)
  if (pageType.type === "category" && Array.isArray((pageType.object as PageTypeObject)?.subcategories)) {
    const ids = new Set(
      (pageType.object as NonNullable<PageTypeObject>).subcategories!.flatMap((sub) => sub.products || [])
    );
    return toProductOptions(
      productProperties.filter((option) => ids.has(option._id))
    );
  }

  if (pageType.type === "sub_category" && Array.isArray((pageType.object as PageTypeObject)?.products)) {
    const ids = new Set((pageType.object as NonNullable<PageTypeObject>).products!.map((p) => p._id));
    return toProductOptions(
      productProperties
        .filter((option) => ids.has(option._id))
        .filter((prod) => prod.product.linked.length > 0)
        .sort((x, y) =>
          x.product.sponsored === y.product.sponsored
            ? 0
            : x.product.sponsored
            ? -1
            : 1
        )
    );
  }

  return toProductOptions(productProperties);
}
